/**
 * PromptClinic 페이지
 * P-C-T-F 프레임워크로 프롬프트 분석 및 개선
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
} from '@mui/material';
import {
  AutoAwesome as SparklesIcon,
  Warning as AlertTriangleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  Image as ImageIcon,
  Message as MessageSquareIcon,
  School as GraduationCapIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { promptClinicAPI } from '../../services/api';

const PCTF_NAMES = {
  P: '역할(P)',
  C: '맥락(C)',
  T: '작업(T)',
  F: '형식(F)',
};

const PCTF_ELEMENTS = ['P', 'C', 'T', 'F'];

const LEVEL_INFO = {
  elementary: { label: '초중등', color: 'primary' },
  highschool: { label: '고등학교', color: 'info' },
  university: { label: '대학교', color: 'secondary' },
};

// Orchestrator별 종류 옵션 매핑
const TYPE_OPTIONS_BY_ORCHESTRATOR = {
  'purpose-definition-frame': [
    { value: 'pctf', label: 'P-C-T-F' },
    { value: 'purpose-context', label: 'Purpose-Context' },
    { value: 'goal-oriented', label: 'Goal-Oriented' },
  ],
  'structure-grammar': [
    { value: 'pctf', label: 'P-C-T-F' },
    { value: 'syntax-based', label: 'Syntax-Based' },
    { value: 'semantic', label: 'Semantic' },
  ],
  'prompt-patterns': [
    { value: 'pctf', label: 'P-C-T-F' },
    { value: 'basic', label: 'Basic' },
    { value: 'advanced', label: 'Advanced' },
  ],
  'thinking-control-techniques': [
    { value: 'pctf', label: 'P-C-T-F' },
    { value: 'chain-of-thought', label: 'Chain-of-Thought' },
    { value: 'tree-of-thought', label: 'Tree-of-Thought' },
    { value: 'self-consistency', label: 'Self-Consistency' },
  ],
};

export default function PromptClinicPage() {
  const [searchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [level, setLevel] = useState('auto');
  const [orchestrator, setOrchestrator] = useState('prompt-patterns');
  const [type, setType] = useState('pctf');
  const [includeVisualAid, setIncludeVisualAid] = useState(true);
  const [generateResponse, setGenerateResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedExample, setExpandedExample] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [savedResultsOpen, setSavedResultsOpen] = useState(false);
  const [savedResults, setSavedResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // 단일 분석 방식 선택 (자동 감지 포함)
  const [analysisMode, setAnalysisMode] = useState('auto');

  // URL 파라미터에서 저장된 결과 로드
  useEffect(() => {
    const savedId = searchParams.get('saved');
    if (savedId) {
      handleLoadResult(parseInt(savedId));
    }
  }, [searchParams]);

  // 분석 방식 변경 시 orchestrator/type 자동 매핑
  const ANALYSIS_MODES = [
    { value: 'auto', label: '자동 감지', orchestrator: 'prompt-patterns', type: 'pctf' },
    { value: 'pctf', label: 'P-C-T-F 구조 분석', orchestrator: 'purpose-definition-frame', type: 'pctf' },
    { value: 'chain-of-thought', label: 'Chain-of-Thought', orchestrator: 'thinking-control-techniques', type: 'chain-of-thought' },
    { value: 'tree-of-thought', label: 'Tree-of-Thought', orchestrator: 'thinking-control-techniques', type: 'tree-of-thought' },
    { value: 'prompt-patterns', label: 'Prompt Patterns', orchestrator: 'prompt-patterns', type: 'advanced' },
    { value: 'goal-oriented', label: '목적 중심 분석', orchestrator: 'purpose-definition-frame', type: 'goal-oriented' },
  ];

  const handleAnalysisModeChange = (mode) => {
    setAnalysisMode(mode);
    const selected = ANALYSIS_MODES.find(m => m.value === mode);
    if (selected) {
      setOrchestrator(selected.orchestrator);
      setType(selected.type);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      setSnackbar({ open: true, message: '질문을 입력해주세요.', severity: 'warning' });
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setExpandedExample(null);

    try {
      const response = await promptClinicAPI.optimize({
        question: question.trim(),
        level,
        orchestrator,
        type,
        include_visual_aid: includeVisualAid,
        generate_response: generateResponse,
      });

      if (response.data.success) {
        setResult(response.data.data);
        setSnackbar({ open: true, message: '프롬프트가 분석되었습니다!', severity: 'success' });
      } else {
        setError(response.data.error || '분석 중 오류가 발생했습니다.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '분석 중 오류가 발생했습니다.';
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setSnackbar({ open: true, message: '클립보드에 복사되었습니다!', severity: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setSnackbar({ open: true, message: '복사에 실패했습니다.', severity: 'error' });
    }
  };

  // Orchestrator 변경 시 종류 옵션 업데이트
  const handleOrchestratorChange = (newOrchestrator) => {
    setOrchestrator(newOrchestrator);
    const availableTypes = TYPE_OPTIONS_BY_ORCHESTRATOR[newOrchestrator] || [];
    // 현재 선택된 종류가 새로운 옵션에 없으면 첫 번째 옵션으로 설정
    if (availableTypes.length > 0 && !availableTypes.find(t => t.value === type)) {
      setType(availableTypes[0].value);
    }
  };

  // 현재 Orchestrator에 사용 가능한 종류 옵션
  const availableTypes = TYPE_OPTIONS_BY_ORCHESTRATOR[orchestrator] || [
    { value: 'pctf', label: 'P-C-T-F' },
    { value: 'basic', label: 'Basic' },
    { value: 'advanced', label: 'Advanced' },
  ];

  const levelInfo = result ? LEVEL_INFO[result.detected_level] : null;

  // 결과 저장
  const handleSave = async () => {
    if (!result) return;

    const title = question.length > 50 ? question.substring(0, 50) + '...' : question;
    
    setSaving(true);
    try {
      const inputData = {
        question,
        level,
        orchestrator,
        type,
        include_visual_aid: includeVisualAid,
        generate_response: generateResponse,
      };

      await promptClinicAPI.save(title, inputData, result);
      setSnackbar({ open: true, message: '결과가 저장되었습니다!', severity: 'success' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || '저장 중 오류가 발생했습니다.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 저장된 결과 목록 조회
  const handleLoadSavedResults = async () => {
    setSavedResultsOpen(true);
    setLoadingSaved(true);
    try {
      const response = await promptClinicAPI.getSaved();
      if (response.data.success) {
        setSavedResults(response.data.data.items || []);
      }
    } catch (err) {
      setSnackbar({ open: true, message: '저장된 결과를 불러오는 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setLoadingSaved(false);
    }
  };

  // 저장된 결과 로드
  const handleLoadResult = async (id) => {
    try {
      const response = await promptClinicAPI.getSavedById(id);
      if (response.data.success) {
        setResult(response.data.data.result_data);
        setQuestion(response.data.data.input_data.question || '');
        setLevel(response.data.data.input_data.level || 'auto');
        setOrchestrator(response.data.data.input_data.orchestrator || 'prompt-patterns');
        setType(response.data.data.input_data.type || 'pctf');
        setIncludeVisualAid(response.data.data.input_data.include_visual_aid !== false);
        setGenerateResponse(response.data.data.input_data.generate_response || false);
        setSavedResultsOpen(false);
        setSnackbar({ open: true, message: '결과가 로드되었습니다!', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: '결과를 불러오는 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  // 저장된 결과 삭제
  const handleDeleteResult = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('이 결과를 삭제하시겠습니까?')) return;

    try {
      await promptClinicAPI.deleteSaved(id);
      setSavedResults(savedResults.filter(r => r.id !== id));
      setSnackbar({ open: true, message: '결과가 삭제되었습니다.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: '삭제 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {/* 헤더 */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GraduationCapIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #00d4ff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            PromptClinic
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
          P-C-T-F 프레임워크로 프롬프트의 문제점을 분석하고 개선안을 제안합니다
        </Typography>
      </Box>

      {/* 입력 폼 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SparklesIcon sx={{ color: 'primary.main' }} />
              <Typography variant="body2" fontWeight="medium">
                분석할 프롬프트를 입력하세요
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder='예: "인공지능이 뭐야?" → P-C-T-F 분석 후 개선된 예시 제공'
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'monospace',
                },
              }}
            />
            {question && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Chip
                  label="• Ready"
                  size="small"
                  color="success"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>분석 방식</InputLabel>
                <Select
                  value={analysisMode}
                  onChange={(e) => handleAnalysisModeChange(e.target.value)}
                  label="분석 방식"
                >
                  {ANALYSIS_MODES.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 고급 옵션 토글 */}
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAdvanced(!showAdvanced)}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              {showAdvanced ? '▼ 고급 옵션 접기' : '▶ 고급 옵션 펼치기'}
            </Button>
          </Box>

          {showAdvanced && (
            <Box sx={{ mb: 3 }}>
              {/* 수준 (Chip 방식) */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>수준</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {[
                    { value: 'auto', label: '자동 감지' },
                    { value: 'elementary', label: '초중등' },
                    { value: 'highschool', label: '고등학교' },
                    { value: 'university', label: '대학교' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      color={level === opt.value ? 'primary' : 'default'}
                      variant={level === opt.value ? 'filled' : 'outlined'}
                      onClick={() => setLevel(opt.value)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Orchestrator + 종류 (표 + Chip 방식) */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>분석 관점 (What)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>분석 기법 (How)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { key: 'purpose-definition-frame', label: '구조 분석', types: [
                        { value: 'pctf', label: 'P-C-T-F' },
                        { value: 'syntax-based', label: 'Syntax-Based' },
                        { value: 'semantic', label: 'Semantic' },
                      ]},
                      { key: 'prompt-patterns', label: '패턴 분석', types: [
                        { value: 'basic', label: 'Basic Patterns' },
                        { value: 'advanced', label: 'Advanced Patterns' },
                      ]},
                      { key: 'thinking-control-techniques', label: '추론 기법', types: [
                        { value: 'chain-of-thought', label: 'CoT' },
                        { value: 'tree-of-thought', label: 'ToT' },
                        { value: 'self-consistency', label: 'Self-Consistency' },
                      ]},
                      { key: 'structure-grammar', label: '목적 분석', types: [
                        { value: 'purpose-context', label: 'Purpose-Context' },
                        { value: 'goal-oriented', label: 'Goal-Oriented' },
                      ]},
                    ].map((row) => (
                      <TableRow
                        key={row.key}
                        hover
                        selected={orchestrator === row.key}
                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'primary.light' } }}
                        onClick={() => { setOrchestrator(row.key); setType(row.types[0].value); }}
                      >
                        <TableCell sx={{ fontWeight: orchestrator === row.key ? 'bold' : 'normal' }}>
                          {row.label}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {row.types.map((t) => (
                              <Chip
                                key={t.value}
                                label={t.label}
                                size="small"
                                color={orchestrator === row.key && type === t.value ? 'primary' : 'default'}
                                variant={orchestrator === row.key && type === t.value ? 'filled' : 'outlined'}
                                onClick={(e) => { e.stopPropagation(); setOrchestrator(row.key); setType(t.value); }}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeVisualAid}
                  onChange={(e) => setIncludeVisualAid(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ImageIcon fontSize="small" />
                  <span>시각 자료</span>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={generateResponse}
                  onChange={(e) => setGenerateResponse(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MessageSquareIcon fontSize="small" />
                  <span>AI 답변</span>
                </Box>
              }
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading || !question.trim()}
              startIcon={isLoading ? <CircularProgress size={20} /> : <SparklesIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
              py: 1.5,
            }}
          >
            {isLoading ? (generateResponse ? 'AI 응답 생성 중...' : '분석 중...') : '프롬프트 분석하기'}
          </Button>
          </Box>

          {/* 결과 저장 및 내 분석 결과 버튼 */}
          {result && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                sx={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                  },
                  boxShadow: '0 4px 15px rgba(168, 85, 247, 0.4)',
                }}
              >
                {saving ? '저장 중...' : '결과 저장'}
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              disabled={loadingSaved}
              startIcon={loadingSaved ? <CircularProgress size={20} /> : <HistoryIcon />}
              onClick={handleLoadSavedResults}
              sx={{
                borderColor: '#a855f7',
                color: '#a855f7',
                '&:hover': {
                  borderColor: '#9333ea',
                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                },
              }}
            >
              내 분석 결과
            </Button>
          </Box>
        </form>
      </Paper>

      {/* 에러 메시지 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 결과 */}
      {result && (
        <Box sx={{ space: 3 }}>
          {/* 문제점 분석 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AlertTriangleIcon />
                &quot;{result.prompt_analysis?.original_prompt || question}&quot; 프롬프트의 문제점
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  P-C-T-F 완성도
                </Typography>
                <Chip
                  label={`${((result.prompt_analysis?.completeness_score || 0) * 100).toFixed(0)}%`}
                  color={
                    (result.prompt_analysis?.completeness_score || 0) >= 0.75
                      ? 'success'
                      : (result.prompt_analysis?.completeness_score || 0) >= 0.5
                      ? 'warning'
                      : 'error'
                  }
                  size="small"
                />
              </Box>
            </Box>

            {/* P-C-T-F 상태 표시 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {PCTF_ELEMENTS.map((element) => {
                const isMissing = result.prompt_analysis?.missing_elements?.includes(element) || false;
                return (
                  <Grid item xs={6} sm={3} key={element}>
                    <Card
                      variant="outlined"
                      sx={{
                        textAlign: 'center',
                        bgcolor: isMissing ? 'error.light' : 'success.light',
                        borderColor: isMissing ? 'error.main' : 'success.main',
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6" color={isMissing ? 'error' : 'success'}>
                            {element}
                          </Typography>
                          {isMissing ? (
                            <XCircleIcon color="error" />
                          ) : (
                            <CheckCircleIcon color="success" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {PCTF_NAMES[element]}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* 문제점 상세 테이블 */}
            {result.prompt_analysis?.issues && result.prompt_analysis.issues.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlertTriangleIcon color="warning" />
                  문제점 분석
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>문제 유형</TableCell>
                        <TableCell>설명</TableCell>
                        <TableCell>왜 문제인가?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.prompt_analysis.issues.map((issue, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip label={issue.issue_type} color="warning" size="small" />
                          </TableCell>
                          <TableCell>{issue.description}</TableCell>
                          <TableCell>{issue.why_problem}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* 완벽한 경우 */}
            {(!result.prompt_analysis?.issues || result.prompt_analysis.issues.length === 0) && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                프롬프트가 P-C-T-F 요소를 모두 포함하고 있습니다! 🎉
              </Alert>
            )}
          </Paper>

          {/* 개선된 프롬프트 예시 */}
          {result.prompt_analysis?.improved_examples && result.prompt_analysis.improved_examples.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" color="success.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                개선된 프롬프트 예시
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                맥락에 따라 올바르게 작성된 프롬프트 예시입니다. 클릭하여 P-C-T-F 분해를 확인하세요.
              </Typography>
              
              <Box sx={{ space: 2 }}>
                {result.prompt_analysis.improved_examples.map((example, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedExample === index}
                    onChange={() => setExpandedExample(expandedExample === index ? null : index)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Chip
                          label={`시나리오 ${index + 1}`}
                          color="primary"
                          size="small"
                        />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {example.scenario_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                          - {example.scenario_description}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ space: 2 }}>
                        {/* 개선된 프롬프트 */}
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.light', borderColor: 'success.main' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold" color="success.dark">
                              개선된 프롬프트
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(example.improved_prompt)}
                              color={copied ? 'success' : 'default'}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              m: 0,
                              fontSize: '0.875rem',
                            }}
                          >
                            {example.improved_prompt}
                          </Typography>
                        </Paper>

                        {/* P-C-T-F 분해 */}
                        <Grid container spacing={2}>
                          {example.pctf_breakdown && Object.entries(example.pctf_breakdown).map(([key, value]) => (
                            <Grid item xs={12} sm={6} key={key}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip
                                      label={key}
                                      size="small"
                                      color="primary"
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      {PCTF_NAMES[key]}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2">{value}</Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Paper>
          )}

          {/* 최적화된 프롬프트 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SparklesIcon color="primary" />
                자동 생성된 프롬프트
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {levelInfo && (
                  <Chip
                    icon={<GraduationCapIcon />}
                    label={levelInfo.label}
                    color={levelInfo.color}
                    size="small"
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleCopy(result.optimized_prompt)}
                  color={copied ? 'success' : 'default'}
                >
                  <CopyIcon />
                </IconButton>
              </Box>
            </Box>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Typography
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                }}
              >
                {result.optimized_prompt}
              </Typography>
            </Paper>
          </Paper>

          {/* LLM 응답 */}
          {result.llm_response && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MessageSquareIcon color="info" />
                  AI 응답
                </Typography>
                {result.llm_configured === false && (
                  <Chip label="Mock 응답" color="warning" size="small" />
                )}
              </Box>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{result.llm_response}</Typography>
              </Paper>
            </Paper>
          )}

          {/* Pro-Tip & 시각 자료 */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightbulbIcon color="warning" />
                  Pro-Tip
                </Typography>
                <Typography variant="body2">{result.pro_tip}</Typography>
              </Paper>
            </Grid>
            {result.visual_aid_suggestion && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon color="info" />
                    시각 자료 제안
                  </Typography>
                  <Typography variant="body2">{result.visual_aid_suggestion}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* 푸터 */}
      <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          P-C-T-F Framework: Persona • Context • Task • Format
        </Typography>
      </Box>

      {/* 저장된 결과 목록 다이얼로그 */}
      <Dialog
        open={savedResultsOpen}
        onClose={() => setSavedResultsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>내 분석 결과</DialogTitle>
        <DialogContent>
          {loadingSaved ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : savedResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              저장된 결과가 없습니다.
            </Typography>
          ) : (
            <List>
              {savedResults.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleLoadResult(item.id)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.title}
                    secondary={new Date(item.created_at).toLocaleString('ko-KR')}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleDeleteResult(item.id, e)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSavedResultsOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
